import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {InstallBidService} from "../../core/services/install-bid.service";
import {ActivatedRoute} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ProjectInstallBidDetail} from "../../core/models/project-install-bid-detail.interface";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {RequestStatusEnum} from "../../core/enums/request-status.enum";
import {ProjectInstallBidRequest} from "../../core/models/project-install-bid-request.interface";
import {InstallAskQuestionDialogComponent} from "../install-ask-question-dialog/install-ask-question-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {InstallerCrew} from "../../core/models/installer-crew.interface";
import {ConfirmationDialogComponent} from "../../shared/components/confirmation-dialog/confirmation-dialog.component";
import {differenceInDays, isFuture, isToday, startOfDay, subWeeks} from "date-fns";

@Component({
  selector: 'app-install-bid',
  templateUrl: './install-bid.component.html',
  styleUrls: ['./install-bid.component.scss']
})
export class InstallBidComponent implements OnInit, OnChanges {
  @Input() projectInstallBidDetails: ProjectInstallBidDetail;
  @Input() installerCrew: InstallerCrew[];
  @Input() multiView: boolean = false;
  @Output('crewChangeEmitter') crewChangeEmitter = new EventEmitter<number | undefined>();

  installBidId: number = 0;
  installerId: number = 0;

  busy: boolean = false;
  editMode: boolean = false;
  locked: boolean = false;
  enableEditCrew: boolean = false;
  crewEditMode: boolean = false;
  crewBusy: boolean = false;

  dueDate: Date;

  selectedCrew: InstallerCrew;
  installCrew: InstallerCrew | undefined;

  bidRequestForm = new FormGroup({
    bidAmount: new FormControl('', [Validators.required, Validators.pattern('^[1-9]\\d*$')])
  });

  constructor(
    private route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog,
    private installBidService: InstallBidService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
        if(changes['projectInstallBidDetails']) {
          this.installBidId = this.projectInstallBidDetails.id;
          this.bidSetup();
        }
    }


  ngOnInit() {
    let currentRoute = this.route;
    while (currentRoute.children[0]) {
      currentRoute = currentRoute.children[0];
    }
    this.route.parent?.paramMap.subscribe(parentParams => {
      this.installerId = +parentParams.get('installerId')!;
    });

    this.route.paramMap.subscribe(params => {
      let installId = +params.get('installId')!;
      if(installId) {
        this.installBidId = installId;
      }
      if (!this.projectInstallBidDetails) {
        this.getInstallBidDetails();
      } else {
        this.bidSetup();
      }
    });
  }

  bidSetup() {
    this.editMode = true;
    if(this.projectInstallBidDetails.request.bidAmount) {
      this.bidRequestForm.controls.bidAmount.setValue(this.projectInstallBidDetails.request.bidAmount.toString());
      this.editMode = false;
    } else {
      this.bidRequestForm.controls.bidAmount.setValue('');
      if(this.projectInstallBidDetails.request.bidStatus === RequestStatusEnum.WITHDRAWN ||
        this.projectInstallBidDetails.request.bidStatus === RequestStatusEnum.NOT_BIDDING) {
        this.editMode = false;
      }
    }
    if(this.projectInstallBidDetails.request.bidStatus === RequestStatusEnum.ACCEPTED ||
      this.projectInstallBidDetails.request.bidStatus === RequestStatusEnum.REJECTED) {
      this.locked = true;
      if(this.projectInstallBidDetails.request.bidStatus === RequestStatusEnum.ACCEPTED) {
        this.enableEditCrew = true;
        if(!this.installerCrew) {
          this.crewBusy = true;
          this.installBidService.getCrew(this.installerId).subscribe({
            next: (crew) => {
              this.installerCrew = crew;
              this.selectCrew();
              this.crewBusy = false;
            },
            error: (err) => {
              this._snackBar.open('Get Crew Failed: ' + err.error.message);
              this.crewBusy = false;
            }
          });
        } else {
          this.selectCrew();
        }
        if(!this.projectInstallBidDetails.request.bidCrewId) {
          this.crewEditMode = true;
        }
      }
    } else {
      this.locked = false;
      this.enableEditCrew = false;
      this.crewEditMode = false;
    }
    if(this.projectInstallBidDetails.bidDate) {
      this.dueDate = startOfDay(new Date(this.projectInstallBidDetails.bidDate));
    } else if(this.projectInstallBidDetails.project.installDate) {
      this.dueDate = startOfDay(subWeeks(new Date(this.projectInstallBidDetails.project.installDate), 2));
    }
    if(!isToday(this.dueDate) && !isFuture(this.dueDate)) {
      this.locked = true;
    }

  }

  selectCrew() {
    this.installCrew = this.installerCrew.find((crew) => crew.id === this.projectInstallBidDetails.request.bidCrewId);
    if (this.installCrew) {
      this.selectedCrew = this.installCrew;
    }
  }

  getInstallBidDetails() {
    this.busy = true;
    this.installBidService.findOne(this.installerId, this.installBidId).subscribe({
      next: (projectInstallBidDetails) => {
        this.projectInstallBidDetails = projectInstallBidDetails;
        this.bidSetup();
        this.busy = false;
      },
      error: (err) => {
        this._snackBar.open('Get Bid Request Failed: ' + err.error.message);
        this.busy = false;
      }
    });
  }

  downloadFile(event: Event, fileName: string) {
    event.stopPropagation();
      this.installBidService.findAttachment(this.installerId, this.installBidId, fileName).subscribe((response) => {
        this.fileDownload(response, fileName);
      });
  }

  fileDownload(response: string, fileName: string) {
    //window.open() was opening then closing the tab, this way just simply downloads the file.
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = response;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
  }

  editForm() {
    this.editMode = true;
  }

  sendBidAmount() {
    this.busy = true;
    let bid: ProjectInstallBidRequest = {};
    if(this.bidRequestForm.controls.bidAmount.value) {
      bid.bidAmount = parseFloat(this.bidRequestForm.controls.bidAmount.value);
    }
    this.installBidService.acceptBid(this.installerId, this.installBidId, bid).subscribe({
      next: (projectInstallBidRequest) => {
        this.projectInstallBidDetails.request = projectInstallBidRequest;
        this.editMode = false;
        this.busy = false;
      },
      error: (err) => {
        this._snackBar.open('Send Bid Failed: ' + err.error.message);
        this.busy = false;
      }
    });
  }

  retractInstall() {
    const confirm = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Retract Bid?',
        message: 'Your Bid will be retracted. Are you sure?'
      }
    });
    confirm.afterClosed().subscribe((result) => {
      if (result) {
        this.busy = true;
        this.installBidService.retractBid(this.installerId, this.installBidId).subscribe({
          next: (projectInstallBidRequest) => {
            this.projectInstallBidDetails.request = projectInstallBidRequest;
            this.bidRequestForm.controls.bidAmount.setValue('');
            this.editMode = false;
            this.busy = false;
          },
          error: (err) => {
            this._snackBar.open('Retract Bid Failed: ' + err.error.message);
            this.busy = false;
          }
        });
      }
    });
  }

  noBid() {
    this.busy = true;
    this.installBidService.noBid(this.installerId, this.installBidId).subscribe({
      next: (projectInstallBidRequest) => {
        this.projectInstallBidDetails.request = projectInstallBidRequest;
        this.bidRequestForm.controls.bidAmount.setValue('');
        this.editMode = false;
        this.busy = false;
      },
      error: (err) => {
        this._snackBar.open('Retract Bid Failed: ' + err.error.message);
        this.busy = false;
      }
    });
  }

  cancelEdit() {
    if(this.projectInstallBidDetails.request.bidAmount) {
      this.bidRequestForm.controls.bidAmount.setValue(this.projectInstallBidDetails.request.bidAmount.toString());
    } else {
      this.bidRequestForm.controls.bidAmount.setValue('');
    }
    this.editMode = false;
  }

  askQuestion() {
    let config: any = {
      width: '950px',
      maxWidth: '90vw',
    };
    if (!this.busy) {
      const confirm = this.dialog.open(InstallAskQuestionDialogComponent, config);
      confirm.afterClosed().subscribe((result) => {
        if (result) {
          this.busy = true;
          this.installBidService.submitQuestion(this.installerId, this.installBidId, result).subscribe({
            next: (projectInstallQuestions) => {
              this.projectInstallBidDetails.questions = projectInstallQuestions;
              this.busy = false;
            },
            error: (err) => {
              this._snackBar.open('Submit Question Failed: ' + err.error.message);
              this.busy = false;
            }
          });
        }
      });
    }
  }

  getQuestions() {
    this.busy = true;
    this.installBidService.getQuestions(this.installerId, this.installBidId).subscribe({
      next: (projectInstallQuestions) => {
        this.projectInstallBidDetails.questions = projectInstallQuestions;
        this.busy = false;
      },
      error: (err) => {
        this._snackBar.open('Get Questions Failed: ' + err.error.message);
        this.busy = false;
      }
    });
  }

  handleUpdateQuestionEmitter() {
    this.getQuestions();
  }

  editCrew() {
    this.crewEditMode = true;
  }

  cancelEditCrew() {
    this.crewEditMode = false;
  }

  saveCrew() {
    this.crewBusy = true;
      this.installBidService.updateCrew(this.installerId, this.installBidId, this.selectedCrew).subscribe({
        next: () => {
          this.installCrew = this.selectedCrew;
          this.crewChangeEmitter.emit(this.installCrew?.id);
          this.crewBusy = false;
          this.crewEditMode = false;
        },
        error: (err) => {
          this.crewBusy = false;
          this._snackBar.open('Save Crew Failed: ' + err.error.message);
        }
      });
  }

  get complianceWarning(): string {
    let warning = '';
    this.projectInstallBidDetails.requiredCompliance?.forEach(compliance => {
      if(!compliance.status || compliance.status !== "VALID" || !compliance.expiryDate || new Date(compliance.expiryDate) < new Date(this.projectInstallBidDetails.project.installDate.toString().substring(0, 10))) {
        warning += compliance.codeName + ' is ';
        if(!compliance.status || compliance.status !== "VALID") {
          warning += 'not VALID. ';
        } else {
          warning += 'EXPIRED. ';
        }
      }
    });
    if(warning > '') {
      warning += 'Your bids will not be accepted until valid documents are provided and verified. Click here to upload new documents.';
    }
    return warning;
  }

  get expiredDays() {
    if(isToday(this.dueDate)) {
      return 'today';
    }
    if(!isFuture(this.dueDate)) {
      return 'expired';
    }
    let days = differenceInDays(this.dueDate, new Date()) + 1;
    if(days > 1) {
      return days + ' days'
    }
    return days + ' day';
  }

}
